/**
 * Date utility functions for consistent date handling in the frontend
 * Prevents timezone shifts when displaying date-only fields
 */

import { formatDate } from "./formatters";

/**
 * Parse a date string (YYYY-MM-DD) without timezone conversion
 * Use this for date-only fields to prevent off-by-one day errors
 *
 * @example
 * // Wrong: new Date("2025-10-07") -> treats as UTC, shifts timezone
 * // Right: parseDateOnly("2025-10-07") -> stays as Oct 7 in local timezone
 */
export function parseDateOnly(dateString: string): Date {
  if (!dateString) return new Date();

  // Handle full ISO timestamps (e.g. "2026-06-01T00:00:00.000Z") — take only YYYY-MM-DD
  const datePart = dateString.includes("T")
    ? dateString.split("T")[0]
    : dateString;
  const [year, month, day] = datePart.split("-").map(Number);
  // Month is 0-indexed in JavaScript Date
  return new Date(year, month - 1, day);
}

/**
 * Format a date string (YYYY-MM-DD) for display without timezone shifts.
 *
 * Delegates to the canonical `formatDate` so date-only fields spell a date the
 * same way the rest of the app does — this used to emit "10/7/2025" while
 * neighbouring components emitted "Oct 7". The old `locale` parameter is gone:
 * no call site ever passed one, and the app deliberately renders one fixed
 * en-US format rather than whatever the browser happens to be set to.
 *
 * @example
 * formatDateOnly("2025-10-07") -> "Oct 7, 2025"
 */
export function formatDateOnly(dateString: string): string {
  if (!dateString) return "";
  return formatDate(parseDateOnly(dateString));
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
export function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Check if two date strings represent the same day
 */
export function isSameDay(dateString1: string, dateString2: string): boolean {
  const date1 = parseDateOnly(dateString1);
  const date2 = parseDateOnly(dateString2);

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
